import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { ThemeProvider, CssBaseline, useMediaQuery } from '@mui/material';
import { createAppTheme } from './theme';

type ColorMode = 'light' | 'dark';
const STORAGE_KEY = 'utilibill-color-mode';

interface ThemeContextValue {
  mode: ColorMode;
  toggleMode: () => void;
}

const ThemeCtx = createContext<ThemeContextValue>({
  mode: 'light',
  toggleMode: () => {},
});

export function useColorMode(): ThemeContextValue {
  return useContext(ThemeCtx);
}

export function AppThemeProvider({ children }: { children: React.ReactNode }) {
  const prefersDark = useMediaQuery('(prefers-color-scheme: dark)');

  // Initialise from localStorage; fall back to OS preference
  const [mode, setMode] = useState<ColorMode>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored === 'light' || stored === 'dark') return stored;
    } catch {
      // localStorage unavailable (e.g. private mode in some browsers)
    }
    return prefersDark ? 'dark' : 'light';
  });

  // Keep in sync when OS preference changes (only when user hasn't set a preference)
  useEffect(() => {
    try {
      if (!localStorage.getItem(STORAGE_KEY)) {
        setMode(prefersDark ? 'dark' : 'light');
      }
    } catch {
      setMode(prefersDark ? 'dark' : 'light');
    }
  }, [prefersDark]);

  const toggleMode = () => {
    setMode((prev) => {
      const next: ColorMode = prev === 'light' ? 'dark' : 'light';
      try { localStorage.setItem(STORAGE_KEY, next); } catch { /* ignore */ }
      return next;
    });
  };

  const theme = useMemo(() => createAppTheme(mode), [mode]);

  const contextValue = useMemo<ThemeContextValue>(
    () => ({ mode, toggleMode }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [mode],
  );

  return (
    <ThemeCtx.Provider value={contextValue}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ThemeCtx.Provider>
  );
}
